import React, { createContext, useContext, useMemo } from 'react'
import { useParams, useRouteMatch } from 'react-router-dom'

import { deriveCanvasFocus } from './deriveCanvasFocus'
import { CanvasScope } from './types'

const canvasScopeContext = createContext<CanvasScope | undefined>(undefined)

interface CanvasScopeProviderProps {
  /**
   * The page on screen. Passed in rather than read from the route because only
   * the route-level component knows how to resolve it: the page and Section
   * routes carry it in the URL, the Share frame route
   * (`/s/:shareID/f/:frameUUID`) does not and has to look it up from the frame,
   * and the root Preview route falls back to the Preview's first page.
   */
  pageUUID: string
  children: React.ReactNode
}

/**
 * Publishes what the canvas is showing, derived once from the route.
 *
 * Before this, the same question was answered in four places and four ways -
 * route matching in `useCopyLinkShortcut`, `match.params` threaded down as
 * props from the route-level queries, and the renderer's own single-entity
 * state read back in `SharePageCanvas` - each under a different name.
 */
export function CanvasScopeProvider({
  pageUUID,
  children,
}: CanvasScopeProviderProps) {
  const { path } = useRouteMatch()
  const params = useParams<{ frameUUID?: string; sectionUUID?: string }>()

  const { frameUUID, sectionUUID } = params

  const scope = useMemo((): CanvasScope => {
    const focus = deriveCanvasFocus(path, { frameUUID, sectionUUID })

    return {
      pageUUID,
      focus,
      entityUUID: focus.kind === 'page' ? undefined : focus.uuid,
    }
  }, [path, frameUUID, sectionUUID, pageUUID])

  return (
    <canvasScopeContext.Provider value={scope}>
      {children}
    </canvasScopeContext.Provider>
  )
}

/**
 * What the canvas is showing, or undefined outside a canvas view.
 *
 * Optional so that consumers shared with non-canvas surfaces keep working
 * without one; each falls back to the behaviour it had before the scope
 * existed.
 */
export function useCanvasScope(): CanvasScope | undefined {
  return useContext(canvasScopeContext)
}
